import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {Button, Dropdown, Input, message, Modal, Switch, Tooltip, TreeSelect} from 'antd';
import {DeleteOutlined, FolderOpenOutlined, MoreOutlined, PlusOutlined, SearchOutlined} from '@ant-design/icons';
import {buildDirTree, mapDirTreeTitles} from '../../utils/dirTree';
import {isImg} from "../../helpers/img";
import {isValidUrl} from "../../helpers/url";
import {
  copyToClipboardAsync,
  getStoredWatermarkImageUrl,
  getStoredWatermarkText,
  saveWatermarkImageUrl,
  saveWatermarkText,
  urlSafeBase64Encode
} from '../../helpers/clipboard';
import { useTranslation } from '../../hooks/useTranslation';


interface BatchActionsProps {
  selectedIds: string[];
  setSelectedIds: (ids: string[] | ((prev: string[]) => string[])) => void;
  list: any[];
  loadHistory: () => void;
  currentDir: string;
  onDirChange: (dir: string) => void;
  onKeywordChange: (keyword: string) => void;
  onSearch: (kw: string) => void;
}

const BatchActions: React.FC<BatchActionsProps> = ({
  selectedIds, setSelectedIds, list, loadHistory, currentDir, onDirChange, onKeywordChange, onSearch
}) => {
  const { t } = useTranslation();
  const [manualCopyUrl, setManualCopyUrl] = useState<string | null>(null);
  const [addDirModalOpen, setAddDirModalOpen] = useState(false);
  const [newDirName, setNewDirName] = useState('');
  const [newDirParent, setNewDirParent] = useState<string>('');
  const [dirLoading, setDirLoading] = useState(false);
  const [dirList, setDirList] = useState<string[]>([]);
  const [internalDir, setInternalDir] = useState(currentDir);

  const [generateGalleryModalOpen, setGenerateGalleryModalOpen] = useState(false);
  const [galleryTitle, setGalleryTitle] = useState('');
  const [galleryBannerUrl, setGalleryBannerUrl] = useState('');
  const [galleryDescription, setGalleryDescription] = useState('');
  const [galleryAnnouncement, setGalleryAnnouncement] = useState('');
  const [galleryLoading, setGalleryLoading] = useState(false);
  
  // 图集创建成功弹框
  const [gallerySuccessModalOpen, setGallerySuccessModalOpen] = useState(false);
  const [createdGalleryUrl, setCreatedGalleryUrl] = useState('');

  // 批量复制链接加载状态
  const [batchCopyLoading, setBatchCopyLoading] = useState(false);



  // 随机路径开关，持久化到本地存储
  const RANDOM_PATH_KEY = 'imgcloud_random_path_enabled';
  const [randomPathEnabled, setRandomPathEnabled] = useState(() => {
    const stored = localStorage.getItem(RANDOM_PATH_KEY);
    return stored === null ? true : stored === 'true';
  });

  useEffect(() => {
    localStorage.setItem(RANDOM_PATH_KEY, String(randomPathEnabled));
  }, [randomPathEnabled]);

  // 拉取目录列表
  const fetchDirList = async () => {
    setDirLoading(true);
    try {
      const res = await fetch('/img-dir/list');
      const json = await res.json();
      if (json.success) {
        setDirList(json.data || []);
        // 如果当前目录不存在于新列表，自动切换到第一个
        if (!json.data.includes(internalDir)) {
          const nextDir = json.data[0] || '';
          setInternalDir(nextDir);
          onDirChange(nextDir);
        }
      } else {
        message.error(json.message || t('batchActions.getDirectoryFailed'));
      }
    } catch (e) {
      message.error(t('batchActions.getDirectoryFailed'));
    } finally {
      setDirLoading(false);
    }
  };

  useEffect(() => {
    fetchDirList();
    // eslint-disable-next-line
  }, []);

  // 外部 currentDir 变化时同步 internalDir
  useEffect(() => {
    setInternalDir(currentDir);
  }, [currentDir]);

  const formatDirLabel = useCallback((value: string) => {
    if (value === '__all__' || value === '全部目录') return t('common.allDirectories');
    if (value === '__default__' || value === '默认目录') return t('common.defaultDirectory');
    return value;
  }, [t]);

  const dirTreeData = useMemo(() => {
    const tree = buildDirTree(dirList);
    return mapDirTreeTitles(tree, formatDirLabel);
  }, [dirList, formatDirLabel]);

  const parentDirTreeData = useMemo(() => {
    const excludeDirs = ['全部目录', '默认目录', '__all__', '__default__'];
    const filtered = dirList.filter((d) => !excludeDirs.includes(d));
    const tree = buildDirTree(filtered);
    return mapDirTreeTitles(tree, (v) => v);
  }, [dirList]);

  const addDirSubmit = async () => {
    if (!newDirName.trim()) {
      message.warning(t('batchActions.directoryNameRequired'));
      return;
    }
    const dirName = newDirParent
      ? `${newDirParent}/${newDirName.trim().replace(/^\/+|\/+$/g, '')}`
      : newDirName.trim().replace(/\s+/g, '_');
    setDirLoading(true);
    try {
      const res = await fetch('/img-dir/add', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ dirName }),
      });
      const json = await res.json();
      if (!json.success) throw new Error(json.message || t('batchActions.directoryAddFailed'));
      setAddDirModalOpen(false);
      setNewDirName('');
      setNewDirParent('');
      message.success(t('batchActions.directoryAdded'));
      await fetchDirList();
    } catch (e: any) {
      message.error(e?.message || t('batchActions.directoryAddFailed'));
    } finally {
      setDirLoading(false);
    }
  };

  // 删除目录操作
  const handleDeleteDir = async () => {
    if (!internalDir) return;

    const isSystemDir = internalDir === '__default__' || internalDir === '默认目录' || internalDir === '__all__' || internalDir === '全部目录';
    if (isSystemDir) {
      message.warning(t('batchActions.directoryCannotDelete', { dir: (internalDir === '__default__' || internalDir === '默认目录') ? t('common.defaultDirectory') : t('common.allDirectories') }));
      return;
    }

    Modal.confirm({
      title: t('batchActions.deleteDirectoryConfirm', { dir: internalDir }),
      onOk: async () => {
        setDirLoading(true);
        try {
          const res = await fetch('/img-dir/delete', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ dirName: internalDir }),
          });
          const json = await res.json();
          if (!json.success) throw new Error(json.message || t('batchActions.directoryDeleteFailed'));
          message.success(t('batchActions.directoryDeleted'));
          await fetchDirList();
        } catch (e: any) {
          message.error(e?.message || t('batchActions.directoryDeleteFailed'));
        } finally {
          setDirLoading(false);
        }
      },
    });
  };

  // 并发控制函数
  const batchProcess = async (items: any[], processFn: (item: any) => Promise<any>, concurrency: number = 15) => {
    const results: any[] = [];
    const chunks = [];
    
    // 将数组分块，每块大小为并发数
    for (let i = 0; i < items.length; i += concurrency) {
      chunks.push(items.slice(i, i + concurrency));
    }
    
    // 逐块处理，每块内并发执行
    for (let i = 0; i < chunks.length; i++) {
      const chunk = chunks[i];
      const chunkPromises = chunk.map(processFn);
      const chunkResults = await Promise.all(chunkPromises);
      results.push(...chunkResults);
      
      // 如果不是最后一块，等待一小段时间再处理下一块
      if (i < chunks.length - 1) {
        await new Promise(resolve => setTimeout(resolve, 100)); // 100ms 间隔
      }
    }
    
    return results;
  };

  const getSelectedFilesInSelectionOrder = useCallback(() => {
    return selectedIds
      .map(id => list.find(item => item.id === id))
      .filter(Boolean);
  }, [selectedIds, list]);

  // 处理批量复制链接
  const handleBatchCopyLinks = async () => {
    setBatchCopyLoading(true);
    try {
      const selectedFiles = getSelectedFilesInSelectionOrder();
      const urls = selectedFiles.map(item => item.url).filter(Boolean);
      
      if (urls.length === 0) {
        message.warning(t('batchActions.noAvailableLinks'));
        return;
      }

      // 使用并发控制批量调用接口生成短链接
      const shortUrls = await batchProcess(urls, async (url) => {
        try {
          const res = await fetch(`/u/save?url=${encodeURIComponent(url)}`, {
            method: 'POST',
          });
          const json = await res.json();
          if (json.shortId) {
            return json.fullShortUrl || `https://z.wiki/u/${json.shortId}`;
          } else {
            throw new Error(t('batchActions.shortLinkGenerateFailed'));
          }
        } catch (error) {
          console.error('生成短链接失败:', url, error);
          return url; // 如果失败，返回原链接
        }
      }, 15); // 限制并发数为15

      const resultText = shortUrls.join('\n');
      
      await copyToClipboardAsync(resultText, t('batchActions.shortLinksCopied', { count: shortUrls.length }));
    } catch (error) {
      console.error('批量复制链接失败:', error);
      message.error(t('batchActions.batchCopyFailed'));
    } finally {
      setBatchCopyLoading(false);
    }
  };

  // 处理生成图集
  const handleGenerateGallery = async () => {
      if (!isAllImage) {
        message.warning(t('batchActions.onlyImagesCanGenerate'));
        return;
      }
      if (!galleryTitle.trim()) {
          message.warning(t('batchActions.galleryTitleRequired'));
          return;
      }
      
      // 校验Banner URL格式
      if (galleryBannerUrl.trim() && !isValidUrl(galleryBannerUrl.trim())) {
          message.warning(t('batchActions.galleryBannerUrlInvalid'));
          return;
      }

      setGalleryLoading(true);
      const names = selectedIds.map(id => {
        const file = list.find(item => item.id === id);
        return file ? file.url : null;
      }).filter(Boolean);

      // NOTE: The backend API now supports title, imgs, bannerUrl, description, and announcement.
      let res, json;
      try {
        res = await fetch('/gallery/add', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ 
            title: galleryTitle.trim(), 
            imgs: names, 
            bannerUrl: galleryBannerUrl, 
            description: galleryDescription,
            announcement: galleryAnnouncement
          }),
        });
      } catch (e) {
        message.error(t('batchActions.galleryCreateFailedNetwork'));
        setGalleryLoading(false);
        return;
      }

      if (!res.ok) {
        // 尝试解析 message 字段
        try {
          const errJson = await res.json();
          if (errJson && errJson.message) {
            message.error(t('batchActions.galleryCreateFailed', { message: errJson.message }));
            setGalleryLoading(false);
            return;
          }
        } catch (e) {}
        message.error(t('batchActions.galleryCreateFailedStatus', { status: res.status }));
        setGalleryLoading(false);
        return;
      }

      try {
        json = await res.json();
      } catch (e) {
        message.error(t('batchActions.galleryCreateFailedFormat'));
        setGalleryLoading(false);
        return;
      }

      if (!json.success) {
        message.error(t('batchActions.galleryCreateFailed', { message: json.message || t('error.unknownError') }));
        setGalleryLoading(false);
        return;
      }

      const galleryUrl = json.data;

      setGenerateGalleryModalOpen(false);
      setGalleryTitle('');
      setGalleryBannerUrl('');
      setGalleryDescription('');
      setGalleryAnnouncement('');
      setGalleryLoading(false);

      // 显示成功弹框
      setCreatedGalleryUrl(galleryUrl);
      setGallerySuccessModalOpen(true);
      await copyToClipboardAsync(galleryUrl, t('batchActions.galleryLinkCopied'));
  };

  // 下拉菜单项
  const menuItems = [
    {
      key: 'add',
      icon: <PlusOutlined />,
      label: t('batchActions.addDirectory'),
      onClick: () => setAddDirModalOpen(true),
    },
    {
      key: 'delete',
      icon: <DeleteOutlined />,
      label: t('batchActions.deleteDirectory'),
      danger: true,
      disabled: !internalDir,
      onClick: handleDeleteDir,
    },
  ];

  // 目录选择和更多操作合并为 Dropdown.Button
  const dirSelect = (
      <Dropdown.Button
        type="default"
        size="small"
        style={{ width: 'auto' }}
        loading={dirLoading}
        menu={{ items: menuItems }}
        icon={<MoreOutlined />}
        placement="bottomRight"
        onClick={e => e.preventDefault()} // 防止主按钮点击
      >
        <TreeSelect
          style={{ minWidth: 120 }}
          value={internalDir || undefined}
          onChange={dir => {
            (window as any)._dir = dir;
            setInternalDir(dir || '');
            onDirChange(dir || '');
          }}
          placeholder={t('batchActions.selectDirectory')}
          size="small"
          suffixIcon={<FolderOpenOutlined />}
          loading={dirLoading}
          bordered={false}
          dropdownClassName="dir-tree-switcher-right"
          dropdownStyle={{ minWidth: 180 }}
          treeData={dirTreeData}
          treeDefaultExpandAll
          showSearch
          treeNodeFilterProp="title"
          allowClear={false}
        />
      </Dropdown.Button>
  );

  // 判断选中项是否全部为图片
  const isAllImage = selectedIds.every(id => {
    const file = list.find(item => item.id === id);
    if (!file) return false;
    return isImg(file.url || '');
  });

  if (!selectedIds || selectedIds.length === 0) return (
    <div style={{margin: '16px 0', display: 'inline-flex', alignItems: 'center', flexDirection: 'row', justifyContent: 'flex-end'}}>
        {dirSelect}
        <Tooltip title={t('batchActions.randomPathTooltip')}>
          <div style={{ display: 'flex', alignItems: 'center', marginRight: 12, marginLeft: 6 }}>
            <Switch
              checked={randomPathEnabled}
              onChange={setRandomPathEnabled}
              size="small"
              style={{ marginRight: 4 }}
            />
            <span style={{ fontSize: 12, whiteSpace: 'nowrap' }}>
              {randomPathEnabled ? t('batchActions.randomPathEnabled') : t('batchActions.randomPathDisabled')}
            </span>
          </div>
        </Tooltip>
      <Input.Search
        placeholder={t('batchActions.searchPlaceholder')}
        allowClear
        enterButton={<SearchOutlined />}
        size="small"
        onChange={e => {
          onKeywordChange && onKeywordChange(e.target.value);
        }}
        onSearch={onSearch}
      />
      <Modal
        open={addDirModalOpen}
        title={t('batchActions.addDirectory')}
        onCancel={() => {
          setAddDirModalOpen(false);
          setNewDirParent('');
        }}
        okText={t('common.confirm')}
        cancelText={t('common.cancel')}
        onOk={addDirSubmit}
        confirmLoading={dirLoading}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div>
            <div style={{ marginBottom: 4, fontSize: 12, color: 'rgba(0,0,0,0.45)' }}>{t('batchActions.parentDirectory')}</div>
            <TreeSelect
              style={{ width: '100%' }}
              value={newDirParent || undefined}
              onChange={setNewDirParent}
              placeholder={t('batchActions.parentDirectoryPlaceholder')}
              dropdownClassName="dir-tree-switcher-right"
              treeData={parentDirTreeData}
              treeDefaultExpandAll
              showSearch
              treeNodeFilterProp="title"
              allowClear
            />
          </div>
          <div>
            <div style={{ marginBottom: 4, fontSize: 12, color: 'rgba(0,0,0,0.45)' }}>{t('batchActions.directoryName')}</div>
            <Input
              value={newDirName}
              onChange={e => setNewDirName(e.target.value)}
              placeholder={t('batchActions.directoryNamePlaceholder')}
              onPressEnter={addDirSubmit}
            />
          </div>
        </div>
      </Modal>
    </div>
  );

  return (
    <>
      <div style={{margin: '16px 0', display: 'flex', alignItems: 'center', flexDirection: 'row', justifyContent: 'flex-end'}}>
        <div style={{display: 'flex', width: '100%'}}>
        {dirSelect}
        <Tooltip title={t('batchActions.randomPathTooltip')}>
          <div style={{ display: 'flex', alignItems: 'center', marginRight: 12, marginLeft: 6 }}>
            <Switch
              checked={randomPathEnabled}
              onChange={setRandomPathEnabled}
              size="small"
              style={{ marginRight: 4 }}
            />
            <span style={{ fontSize: 12, whiteSpace: 'nowrap' }}>
              {randomPathEnabled ? t('batchActions.randomPathEnabled') : t('batchActions.randomPathDisabled')}
            </span>
          </div>
        </Tooltip>
        </div>
        <Dropdown
          menu={{
            items: [
              // {
              //   key: 'soft-delete',
              //   label: '删除到回收站',
              //   onClick: async () => {
              //     const doDelete = window.confirm(`确定要删除所选的 ${selectedIds.length} 个文件吗？文件将移动到回收站，7 天后自动清理。`);
              //     if (!doDelete) return;
              //     try {
              //       await Promise.all(selectedIds.map(id =>
              //         fetch(`/img/delete?hard=false&id=${id}&uid=${localStorage.getItem('fdd_uid')}`, { method: 'DELETE' })
              //       ));
              //       setSelectedIds([]);
              //       loadHistory();
              //       message.success('所选文件已移动到回收站');
              //     } catch (error) {
              //       message.error('删除失败，请重试');
              //     }
              //   }
              // },
              {
                key: 'hard-delete',
                label: t('batchActions.deleteSelected'),
                danger: true,
                onClick: async () => {
                  const doDelete = window.confirm(t('batchActions.deleteSelectedConfirm', { count: selectedIds.length }));
                  if (!doDelete) return;
                  try {
                    const uid = localStorage.getItem('fdd_uid');
                    const ids = selectedIds.map(id => parseInt(id));
                    const response = await fetch(`/img/batchDelete`, { 
                      method: 'POST',
                      headers: {
                        'Content-Type': 'application/json',
                      },
                      body: JSON.stringify({ 
                        ids,
                        uid,
                        hard: true
                      })
                    });
                    const result = await response.json();
                    
                    if (result.success) {
                      setSelectedIds([]);
                      loadHistory();
                      if (result.successCount === selectedIds.length) {
                        message.success(result.message || t('batchActions.selectedFilesDeleted'));
                      } else {
                        message.warning(result.message || t('batchActions.deletePartialSuccess', { success: result.successCount, total: selectedIds.length }));
                      }
                    } else {
                      message.error(result.message || t('batchActions.deleteFailedRetry'));
                    }
                  } catch (error) {
                    console.error('批量删除失败:', error);
                    message.error(t('batchActions.deleteFailedRetry'));
                  }
                }
              },
            ]
          }}
        >
          <Button
            danger
            size="small"
            icon={<DeleteOutlined />}
            onClick={async () => {
              const doDelete = window.confirm(t('batchActions.deleteSelectedConfirm', { count: selectedIds.length }));
              if (!doDelete) return;
              try {
                const uid = localStorage.getItem('fdd_uid');
                const ids = selectedIds.map(id => parseInt(id));
                const response = await fetch(`/img/batchDelete`, { 
                  method: 'POST',
                  headers: {
                    'Content-Type': 'application/json',
                  },
                  body: JSON.stringify({ 
                    ids,
                    uid,
                    hard: true
                  })
                });
                const result = await response.json();
                
                if (result.success) {
                  setSelectedIds([]);
                  loadHistory();
                  if (result.successCount === selectedIds.length) {
                    message.success(result.message || t('batchActions.selectedFilesDeleted'));
                  } else {
                    message.warning(result.message || t('batchActions.deletePartialSuccess', { success: result.successCount, total: selectedIds.length }));
                  }
                } else {
                  message.error(result.message || t('batchActions.deleteFailedRetry'));
                }
              } catch (error) {
                console.error('批量删除失败:', error);
                message.error(t('batchActions.deleteFailedRetry'));
              }
            }}
            style={{ marginLeft: 8 }}
          >
            {t('batchActions.deleteSelected')}
          </Button>
        </Dropdown>
        <Button
          type="primary"
          size="small"
          style={{ marginLeft: 8 }}
          disabled={!isAllImage}
          title={!isAllImage ? t('batchActions.onlyImagesCanGenerate') : ''}
          onClick={() => setGenerateGalleryModalOpen(true)} // Open modal
        >
          {t('batchActions.generateGallery')}
        </Button>
        <Dropdown
          menu={{
            items: [
              {
                key: 'text',
                label: t('batchActions.textLinks'),
                onClick: async () => {
                  const selectedFiles = getSelectedFilesInSelectionOrder();
                  const text = selectedFiles.map(item => item.url).join('\n');
                  await copyToClipboardAsync(text, t('batchActions.textLinksCopied'));
                }
              },
              {
                key: 'shortlinks',
                label: t('batchActions.shortLinks'),
                onClick: handleBatchCopyLinks,
                disabled: batchCopyLoading,
              },
              {
                key: 'webp',
                label: t('batchActions.losslessCompression'),
                onClick: async () => {
                  const selectedFiles = getSelectedFilesInSelectionOrder();
                  const webpUrls = selectedFiles.map(item => {
                    const [pathname, search] = item.url.split('?');
                    return search ? `${pathname}/webp?${search}` : `${pathname}/webp`;
                  }).join('\n');
                  await copyToClipboardAsync(webpUrls, t('batchActions.losslessCompressionCopied'));
                }
              },
              {
                key: 'overseas',
                label: t('batchActions.overseasAcceleration'),
                onClick: async () => {
                  const selectedFiles = getSelectedFilesInSelectionOrder();
                  const overseasUrls = selectedFiles.map(item => {
                    const urlObj = new URL(item.url);
                    return `https://xland.eu.org${urlObj.pathname}${urlObj.search}`;
                  }).join('\n');
                  await copyToClipboardAsync(overseasUrls, t('batchActions.overseasAccelerationCopied'));
                }
              },
              {
                key: 'thumbnail100',
                label: t('batchActions.thumbnail100px'),
                onClick: async () => {
                  const selectedFiles = getSelectedFilesInSelectionOrder();
                  const thumbnails = selectedFiles.map(item => {
                    const separator = item.url.includes('?') ? '&' : '?';
                    return `${item.url}${separator}imageMogr2/thumbnail/100x`;
                  }).join('\n');
                  await copyToClipboardAsync(thumbnails, t('batchActions.thumbnail100pxCopied'));
                }
              },
              {
                key: 'thumbnail200',
                label: t('batchActions.thumbnail200px'),
                onClick: async () => {
                  const selectedFiles = getSelectedFilesInSelectionOrder();
                  const thumbnails = selectedFiles.map(item => {
                    const separator = item.url.includes('?') ? '&' : '?';
                    return `${item.url}${separator}imageMogr2/thumbnail/200x`;
                  }).join('\n');
                  await copyToClipboardAsync(thumbnails, t('batchActions.thumbnail200pxCopied'));
                }
              },
              {
                key: 'thumbnail300',
                label: t('batchActions.thumbnail300px'),
                onClick: async () => {
                  const selectedFiles = getSelectedFilesInSelectionOrder();
                  const thumbnails = selectedFiles.map(item => {
                    const separator = item.url.includes('?') ? '&' : '?';
                    return `${item.url}${separator}imageMogr2/thumbnail/300x`;
                  }).join('\n');
                  await copyToClipboardAsync(thumbnails, t('batchActions.thumbnail300pxCopied'));
                }
              },
              {
                key: 'thumbnail600',
                label: t('batchActions.thumbnail600px'),
                onClick: async () => {
                  const selectedFiles = getSelectedFilesInSelectionOrder();
                  const thumbnails = selectedFiles.map(item => {
                    const separator = item.url.includes('?') ? '&' : '?';
                    return `${item.url}${separator}imageMogr2/thumbnail/600x`;
                  }).join('\n');
                  await copyToClipboardAsync(thumbnails, t('batchActions.thumbnail600pxCopied'));
                }
              },
              {
                key: 'watermark_default',
                label: t('batchActions.defaultWatermark'),
                onClick: async () => {
                  const storedText = getStoredWatermarkText();
                  const customText = window.prompt(t('batchActions.watermarkTextPrompt'), storedText);
                  if (customText) {
                    if (customText.length > 10) {
                      message.error(t('batchActions.watermarkTextTooLong'));
                      return;
                    }
                    saveWatermarkText(customText);
                    const selectedFiles = getSelectedFilesInSelectionOrder();
                    const watermarks = selectedFiles.map(item => {
                      const separator = item.url.includes('?') ? '&' : '?';
                      const text = urlSafeBase64Encode(customText);
                      return `${item.url}${separator}watermark/2/text/${text}/fontsize/20/fill/IzAwMDAwMA/fontcolor/ffffff/t/50`;
                    }).join('\n');
                    await copyToClipboardAsync(watermarks, t('batchActions.defaultWatermarkCopied'));
                  }
                }
              },
              {
                key: 'watermark_tile',
                label: t('batchActions.tileWatermark'),
                onClick: async () => {
                  const storedText = getStoredWatermarkText();
                  const customText = window.prompt(t('batchActions.watermarkTextPrompt'), storedText);
                  if (customText) {
                    if (customText.length > 10) {
                      message.error(t('batchActions.watermarkTextTooLong'));
                      return;
                    }
                    saveWatermarkText(customText);
                    const selectedFiles = getSelectedFilesInSelectionOrder();
                    const watermarks = selectedFiles.map(item => {
                      const separator = item.url.includes('?') ? '&' : '?';
                      const text = urlSafeBase64Encode(customText);
                      return `${item.url}${separator}watermark/2/text/${text}/fontsize/20/fill/IzAwMDAwMA/fontcolor/ffffff/dissolve/10/gravity/center/batch/1/degree/30`;
                    }).join('\n');
                    await copyToClipboardAsync(watermarks, t('batchActions.tileWatermarkCopied'));
                  }
                }
              },
              {
                key: 'image_watermark',
                label: t('batchActions.imageWatermark'),
                onClick: async () => {
                  const storedImageUrl = getStoredWatermarkImageUrl();
                  const watermarkImageUrl = window.prompt(t('batchActions.watermarkImagePrompt'), storedImageUrl || 'https://z.wiki/autoupload/20230831/l32P.%E5%9B%BE%E7%89%87%E5%8A%A0%E8%BD%BD%E5%A4%B1%E8%B4%A5_%282%29.svg');
                                      if (watermarkImageUrl) {
                      // 验证URL格式
                      if (!watermarkImageUrl.startsWith('http://') && !watermarkImageUrl.startsWith('https://')) {
                        message.error(t('batchActions.watermarkImageInvalid'));
                        return;
                      }

                    saveWatermarkImageUrl(watermarkImageUrl);
                    
                    // 从水印图片URL中截取路径
                    let imageKey = '';
                    try {
                      const urlObj = new URL(watermarkImageUrl);
                      // 提取路径部分，去掉域名
                      imageKey = urlObj.pathname;
                      // 如果路径以/开头，去掉开头的/
                      if (imageKey.startsWith('/')) {
                        imageKey = imageKey.substring(1);
                      }
                    } catch (e) {
                      message.error(t('batchActions.watermarkImageUrlError'));
                      return;
                    }
                    
                    const selectedFiles = getSelectedFilesInSelectionOrder();
                    const encodedImageKey = urlSafeBase64Encode(imageKey);
                    const watermarks = selectedFiles.map(item => {
                      const separator = item.url.includes('?') ? '&' : '?';
                      return `${item.url}${separator}watermark/1/image_key/${encodedImageKey}/gravity/southeast/ws/100/ht/50`;
                    }).join('\n');
                    await copyToClipboardAsync(watermarks, t('batchActions.imageWatermarkCopied'));
                  }
                }
              },
              {
                key: 'html',
                label: t('batchActions.html'),
                onClick: async () => {
                  const selectedFiles = getSelectedFilesInSelectionOrder();
                  const html = selectedFiles.map(item => `<img src="${item.url}" />`).join('\n');
                  await copyToClipboardAsync(html, t('batchActions.htmlCopied'));
                }
              },
              {
                key: 'bbcode',
                label: t('batchActions.bbcode'),
                onClick: async () => {
                  const selectedFiles = getSelectedFilesInSelectionOrder();
                  const bbcode = selectedFiles.map(item => `[img]${item.url}[/img]`).join('\n');
                  await copyToClipboardAsync(bbcode, t('batchActions.bbcodeCopied'));
                }
              },
              {
                key: 'markdown',
                label: t('batchActions.markdown'),
                onClick: async () => {
                  const selectedFiles = getSelectedFilesInSelectionOrder();
                  const md = selectedFiles.map(item => `![](${item.url})`).join('\n');
                  await copyToClipboardAsync(md, t('batchActions.markdownCopied'));
                }
              },
            ]
          }}
        >
          <Button size="small" style={{ marginLeft: 8 }} loading={batchCopyLoading}>{t('batchActions.batchCopy')}</Button>
        </Dropdown>
        <Dropdown
          trigger={["hover"]}
          menu={{
            items: [
              {
                key: 'selectAll',
                label: t('batchActions.selectAll'),
                onClick: () => {
                  setSelectedIds(list.map(item => item.id));
                }
              },
              {
                key: 'invert',
                label: t('batchActions.invert'),
                onClick: () => {
                  setSelectedIds((prev: string[]) => list.map(item => item.id).filter((id: string) => !prev.includes(id)));
                }
              },
              {
                key: 'clear',
                label: t('batchActions.clearSelection'),
                onClick: () => {
                  setSelectedIds([]);
                }
              }
            ]
          }}
        >
          <Button size="small" style={{ marginLeft: 8 }}>{t('batchActions.moreActions')}</Button>
        </Dropdown>
      </div>
      {/* Modal for adding new directory */}
      <Modal
        open={addDirModalOpen}
        title={t('batchActions.addDirectory')}
        onCancel={() => {
          setAddDirModalOpen(false);
          setNewDirParent('');
        }}
        okText={t('common.confirm')}
        cancelText={t('common.cancel')}
        onOk={addDirSubmit}
        confirmLoading={dirLoading}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div>
            <div style={{ marginBottom: 4, fontSize: 12, color: 'rgba(0,0,0,0.45)' }}>{t('batchActions.parentDirectory')}</div>
            <TreeSelect
              style={{ width: '100%' }}
              value={newDirParent || undefined}
              onChange={setNewDirParent}
              placeholder={t('batchActions.parentDirectoryPlaceholder')}
              dropdownClassName="dir-tree-switcher-right"
              treeData={parentDirTreeData}
              treeDefaultExpandAll
              showSearch
              treeNodeFilterProp="title"
              allowClear
            />
          </div>
          <div>
            <div style={{ marginBottom: 4, fontSize: 12, color: 'rgba(0,0,0,0.45)' }}>{t('batchActions.directoryName')}</div>
            <Input
              value={newDirName}
              onChange={e => setNewDirName(e.target.value)}
              placeholder={t('batchActions.directoryNamePlaceholder')}
              onPressEnter={addDirSubmit}
            />
          </div>
        </div>
      </Modal>

      {/* Modal for generating gallery */}
      <Modal
          title={t('batchActions.generateGallery')}
          open={generateGalleryModalOpen}
          onCancel={() => {
            setGenerateGalleryModalOpen(false);
            setGalleryTitle('');
            setGalleryBannerUrl('');
            setGalleryDescription('');
            setGalleryAnnouncement('');
            // 清空成功弹框状态
            setGallerySuccessModalOpen(false);
            setCreatedGalleryUrl('');
          }}
          onOk={handleGenerateGallery}
          confirmLoading={galleryLoading}
          okText={t('batchActions.generate')}
          cancelText={t('common.cancel')}
      >
          <div style={{ marginBottom: 16 }}>
              <label>{t('batchActions.galleryTitleLabel')}</label>
              <Input
                  value={galleryTitle}
                  onChange={e => setGalleryTitle(e.target.value)}
                  placeholder={t('batchActions.galleryTitlePlaceholder')}
              />
          </div>
          <div style={{ marginBottom: 16 }}>
              <label>{t('batchActions.galleryBannerLabel')}</label>
              <Input
                  value={galleryBannerUrl}
                  onChange={e => setGalleryBannerUrl(e.target.value)}
                  placeholder={t('batchActions.galleryBannerUrlPlaceholder')}
              />
          </div>
          <div style={{ marginBottom: 16 }}>
              <label>{t('batchActions.galleryDescriptionLabel')}</label>
              <Input.TextArea
                  value={galleryDescription}
                  onChange={e => setGalleryDescription(e.target.value)}
                  placeholder={t('batchActions.galleryDescriptionPlaceholder')}
                  autoSize={{ minRows: 3, maxRows: 6 }}
                  onPaste={e => e.stopPropagation()}
              />
          </div>
          <div style={{ marginBottom: 16 }}>
              <label>{t('batchActions.galleryAnnouncementLabel')}</label>
              <Input.TextArea
                  value={galleryAnnouncement}
                  onChange={e => setGalleryAnnouncement(e.target.value)}
                  placeholder={t('batchActions.galleryAnnouncementPlaceholder')}
                  autoSize={{ minRows: 2, maxRows: 4 }}
                  onPaste={e => e.stopPropagation()}
              />
          </div>
      </Modal>

      {/* 图集创建成功弹框 */}
      <Modal
        open={gallerySuccessModalOpen}
        title={t('batchActions.galleryCreateSuccess')}
        onCancel={() => {
          setGallerySuccessModalOpen(false);
          setCreatedGalleryUrl('');
        }}
        footer={[
          <Button 
            key="copy" 
            type="primary" 
            onClick={async () => {
              await copyToClipboardAsync(createdGalleryUrl, t('batchActions.galleryLinkCopied'));
            }}
          >
            {t('batchActions.copyLink')}
          </Button>,
                     <Button key="close" onClick={() => {
             setGallerySuccessModalOpen(false);
             setCreatedGalleryUrl('');
           }}>
             {t('common.close')}
           </Button>
        ]}
        width={500}
      >
        <div style={{ textAlign: 'left', padding: '20px 0' }}>
          <div style={{ marginBottom: 24 }}>
            <h4 style={{ margin: '0 0 12px 0', color: '#52c41a' }}>{t('batchActions.galleryLink')}</h4>
            <div style={{ 
              wordBreak: 'break-all', 
              userSelect: 'all', 
              fontSize: 14, 
              padding: 12, 
              background: '#f5f5f5', 
              borderRadius: 6,
              border: '1px solid #e0e0e0'
            }}>
              {createdGalleryUrl}
            </div>
          </div>
          
          <div style={{ marginBottom: 16 }}>
            <h4 style={{ margin: '0 0 12px 0' }}>{t('batchActions.galleryQrCode')}</h4>
            <div style={{ 
              display: 'flex', 
              justifyContent: 'center',
              padding: 16,
              background: '#fafafa',
              borderRadius: 6,
              border: '1px solid #e0e0e0'
            }}>
              <img 
                src={`https://playground.z.wiki/qrcode/generateQRCode?data=${encodeURIComponent(createdGalleryUrl)}`}
                alt={t('batchActions.galleryQrCodeAlt')}
                style={{ 
                  width: 200, 
                  height: 200,
                  border: '1px solid #ddd',
                  borderRadius: 4
                }}
                onError={(e) => {
                  // 如果二维码加载失败，显示备用文本
                  (e.target as HTMLImageElement).style.display = 'none';
                  const parent = (e.target as HTMLImageElement).parentElement;
                  if (parent && !parent.querySelector('.qr-error')) {
                    const errorDiv = document.createElement('div');
                    errorDiv.className = 'qr-error';
                    errorDiv.style.cssText = 'color: #999; font-size: 14px; padding: 40px;';
                    errorDiv.textContent = t('batchActions.galleryQrCodeFailed');
                    parent.appendChild(errorDiv);
                  }
                }}
              />
            </div>
            <div style={{ fontSize: 12, color: '#666', marginTop: 8 }}>
              {t('batchActions.scanQrCodeHint')}
            </div>
          </div>
        </div>
      </Modal>

      <Modal
        open={!!manualCopyUrl}
        title={t('batchActions.manualCopyTitle')}
        onCancel={() => setManualCopyUrl(null)}
        footer={[
          <Button key="close" onClick={() => setManualCopyUrl(null)}>
            {t('common.close')}
          </Button>
        ]}
      >
        <div style={{ wordBreak: 'break-all', userSelect: 'all', fontSize: 16, padding: 8, background: '#f5f5f5', borderRadius: 4 }}>
          {manualCopyUrl}
        </div>
        <div style={{ marginTop: 12, color: '#888' }}>{t('batchActions.manualCopyHint')}</div>
      </Modal>
    </>
  );
};

export default BatchActions; 